/** Shared flora templates/materials, worker placement and tile-owned collision.
 * The curved palm uses a chain of small radial cylinders, never one enormous
 * upright column encompassing the empty space below its crown. */
import * as THREE from 'three';
import type {FloraLibrary} from './library';
import type {ClutterSets} from './clutter';
import type {CoastalData,CoastalKind} from './coastalData';
import {floraHeight,floraTime} from './material';
import {makePooledFloraMesh} from './materialPool';
import {prepareScatterBounds} from './scatterLod';

export interface CoastalCollider {x:number;y:number;z:number;r:number;h:number;minAlong:number}
interface StemPoint {p:[number,number,number];radius:number}

/** The existing solver's radial cylinders approximate short stem segments to
 * within 15cm laterally. Tight lower bounds keep elevated bends passable below. */
export function palmColliders(geo:THREE.BufferGeometry,matrix:THREE.Matrix4,size:number,metadataKey:'palm'|'acacia'='palm'):CoastalCollider[] {
  const stem=geo.userData[metadataKey]?.stem as StemPoint[]|undefined;
  if(!stem?.length)return [];
  const result:CoastalCollider[]=[],a=new THREE.Vector3(),b=new THREE.Vector3(),start=new THREE.Vector3(),end=new THREE.Vector3(),axis=new THREE.Vector3(),delta=new THREE.Vector3(),mid=new THREE.Vector3();
  for(let i=0;i<stem.length-1;i++){
    a.fromArray(stem[i].p).applyMatrix4(matrix);b.fromArray(stem[i+1].p).applyMatrix4(matrix);
    axis.copy(a).normalize();delta.copy(b).sub(a);
    const along=delta.dot(axis),lateral=Math.sqrt(Math.max(0,delta.lengthSq()-along*along));
    const steps=Math.max(1,Math.ceil(Math.abs(along)/1.8),Math.ceil(lateral/.30));
    for(let j=0;j<steps;j++){
      start.copy(a).lerp(b,j/steps);end.copy(a).lerp(b,(j+1)/steps);
      mid.copy(start).add(end).multiplyScalar(.5);axis.copy(mid).normalize();delta.copy(end).sub(start);
      const height=Math.abs(delta.dot(axis)),side=Math.sqrt(Math.max(0,delta.lengthSq()-height*height));
      const t=j/steps,r1=THREE.MathUtils.lerp(stem[i].radius,stem[i+1].radius,t),r2=THREE.MathUtils.lerp(stem[i].radius,stem[i+1].radius,(j+1)/steps);
      // The midpoint defines lateral position; move down only by this segment's
      // true radial height. A 4cm seam overlap prevents gaps between capsules.
      mid.addScaledVector(axis,-height*.5-.04);
      result.push({x:mid.x,y:mid.y,z:mid.z,r:Math.max(r1,r2)*size+side*.5+.035,h:height+.08,minAlong:0});
    }
  }
  return result;
}

export function mountCoastalHabitat(data:CoastalData,library:FloraLibrary,clutter:ClutterSets,far=false) {
  const meshes:THREE.InstancedMesh[]=[],colliders:CoastalCollider[]=[];
  const groups=new Map<string,{geo:THREE.BufferGeometry;coarse?:THREE.BufferGeometry;kind:CoastalKind;items:{matrix:THREE.Matrix4;evidence:object}[]}>();
  const Y=new THREE.Vector3(0,1,0),up=new THREE.Vector3(),pos=new THREE.Vector3(),q=new THREE.Quaternion(),spin=new THREE.Quaternion(),scale=new THREE.Vector3();
  for(const p of data){
    if(far&&p.kind!=='palm')continue;
    const key=`${p.kind}:${p.variant}`;let group=groups.get(key);
    if(!group){
      const geo=p.kind==='palm'?(far?library.variantsFar('palm','summer',2):library.variants('palm','summer',2))[p.variant]:clutter[p.kind][p.variant];
      const coarse=!far?(p.kind==='palm'?library.variantsFar('palm','summer',2)[p.variant]:p.kind==='shrub'?clutter.shrubCoarse[p.variant]:undefined):undefined;
      groups.set(key,group={geo,coarse,kind:p.kind,items:[]});
    }
    up.fromArray(p.normal);q.setFromUnitVectors(Y,up).multiply(spin.setFromAxisAngle(Y,p.yaw));
    pos.fromArray(p.dir).multiplyScalar(p.radius);scale.set(p.size,p.size*(p.kind==='grass'?.72:1),p.size);
    const matrix=new THREE.Matrix4().compose(pos,q,scale);
    group.items.push({matrix,evidence:{...p,position:pos.toArray()}});
    if(!far&&p.kind==='palm')colliders.push(...palmColliders(group.geo,matrix,p.size));
  }
  for(const group of groups.values()){
    const fadeFar=far?0:group.kind==='palm'?950:group.kind==='shrub'?330:240;
    // The botanical stem height is identical in both LODs. Using the two
    // tessellations' leaf bounds would subtly change wind at the crossfade.
    const height=group.kind==='palm'?group.geo.userData.palm.height:floraHeight(group.geo);
    const mesh=makePooledFloraMesh(group.geo,group.items.length,{birth:floraTime(),fadeFar,fadeNear:far?950:0,height});
    group.items.forEach((p,i)=>mesh.setMatrixAt(i,p.matrix));mesh.instanceMatrix.needsUpdate=true;
    Object.assign(mesh.userData,{coastalHabitat:group.kind,coastalFar:far,coastalCover:group.kind!=='palm',fadeFar,fadeNear:far?950:0,
      coverEvidence:group.items.map(p=>p.evidence),scatterLodNear:group.kind==='palm'?220:100,scatterLodFar:group.kind==='palm'?290:135});
    mesh.castShadow=!far&&group.kind==='palm';mesh.receiveShadow=true;mesh.visible=false;
    prepareScatterBounds(mesh,group.coarse);meshes.push(mesh);
  }
  return {meshes,colliders};
}
